/**
 * Where the site actually is, expressed as the environmental parameters a plant responds to.
 *
 * ## The shape of the bet
 *
 * A localized catalog is the obvious thing to build and the wrong thing to build. "Red Oak, zone 6b"
 * and "Red Oak, zone 7a" are not two plants; they are one plant read against two winters. Storing
 * them as two records means every correction to the oak has to be made twice, every new site adds a
 * copy, and the moment the two drift the catalog can no longer say what a red oak *is*.
 *
 * So this module holds no botany at all. It describes a **place** — a latitude, a winter, a frost
 * window, a region — and `FloraResolver` combines it with the one canonical record to derive what
 * that plant does *here*. The canonical record is never rewritten; a site is a set of parameters.
 *
 * ## What a profile is allowed to contain
 *
 * Only things that are true of the ground rather than of the plant, and only things some source can
 * actually supply. USDA zone is the entry point because it is the one climate figure a gardener
 * already knows, but it answers exactly one question — how cold the winter gets — and the fields
 * beside it exist so that timing, heat and season length are never inferred from it alone.
 *
 * Every future variable named in {@link SiteLocalization} is optional and absent today. That is the
 * extension point: a Growing Degree Day model, a chill-hour count or a real weather series drops in
 * as another parameter here, and `FloraResolver` starts using it, without a single flora record or
 * project file changing shape.
 *
 * Pure data and pure functions. Nothing here touches the network — see `PostalZones.ts` for the
 * ZIP→profile lookup and its own honesty about where its numbers come from.
 */

import { DAYS_IN_YEAR, dateFromDayOfYear } from '@/environment/SeasonModel';

/** Bumped when the profile gains a field older documents could not have written. */
export const LOCALIZATION_SCHEMA_VERSION = 1;

/**
 * How much a profile can be trusted, following the convention `geo/SiteData.ts` set.
 *
 * `estimated` is not a lesser grade of `measured` — it is a different thing, and the word appears in
 * the UI wherever the profile does. A ZIP code resolved from the built-in table is an estimate for
 * the middle of a postal district, which is the right answer for choosing plants and the wrong answer
 * for anything that claims to know your garden.
 */
export type LocalizationConfidence = 'measured' | 'estimated' | 'assumed';

export interface LocalizationSource {
  id: string;
  label: string;
  confidence: LocalizationConfidence;
  /** Credit line, where the data has one. */
  attribution?: string;
}

/* --------------------------------- Hardiness --------------------------------- */

export type HardinessBand = 'a' | 'b';

/**
 * A USDA hardiness zone, kept as its two parts rather than as the string "6b".
 *
 * The half-zone matters: 6a and 6b are 2.8 °C apart at the bottom of the winter, which is the
 * difference between a *Prunus laurocerasus* that comes through March and one that does not. Every
 * comparison in the resolver runs on {@link zoneValue}, so a plant's limits and a site's zone are
 * always compared as numbers rather than as parsed labels.
 */
export interface HardinessZone {
  /** 1–13. */
  number: number;
  band: HardinessBand;
}

/** `6b` → 6.5. The single number every hardiness comparison in the app runs on. */
export function zoneValue(zone: HardinessZone): number {
  return zone.number + (zone.band === 'b' ? 0.5 : 0);
}

/** 6.5 → `{ number: 6, band: 'b' }`. Values between half-zones round down to the colder band. */
export function zoneFromValue(value: number): HardinessZone {
  const clamped = Math.min(13.5, Math.max(1, value));
  const number = Math.floor(clamped);
  return { number, band: clamped - number >= 0.5 ? 'b' : 'a' };
}

export function zoneLabel(zone: HardinessZone): string {
  return `${zone.number}${zone.band}`;
}

/** Parse `6b`, `6B`, or `6` (which reads as the colder half). Undefined when unreadable. */
export function parseZone(text: string): HardinessZone | undefined {
  const match = /^\s*(\d{1,2})\s*([ab])?\s*$/i.exec(text);
  if (!match) return undefined;
  const number = Number(match[1]);
  if (!Number.isInteger(number) || number < 1 || number > 13) return undefined;
  return { number, band: (match[2]?.toLowerCase() as HardinessBand | undefined) ?? 'a' };
}

/**
 * Average annual minimum winter temperature, in °C, for a zone.
 *
 * This *is* the zone — the bands are defined as 5 °F (2.8 °C) steps from −60 °F at zone 1a — so the
 * conversion is exact rather than a table lookup. Carried on the profile so a UI can show the figure
 * the zone stands for, and so a future model driven by actual temperatures has a number to compare.
 */
export function zoneMinimumTempC(zone: HardinessZone): number {
  const fahrenheit = -60 + (zoneValue(zone) - 1) * 10;
  return Math.round(((fahrenheit - 32) * 5) / 9);
}

/* ----------------------------------- Regions ---------------------------------- */

/**
 * The regional buckets nativity is expressed in.
 *
 * Coarse on purpose. A native-range polygon is the honest data structure and none of the sources
 * that would fill one are wired up; a dozen regions is enough to answer "is this locally native, or
 * is it from somewhere else entirely", which is the question a planting plan actually asks. The
 * codes are stable strings so flora records that name them keep meaning the same thing when a finer
 * model arrives beside this one.
 */
export type NativeRegionCode =
  | 'northeast'
  | 'mid-atlantic'
  | 'southeast'
  | 'florida'
  | 'midwest'
  | 'great-plains'
  | 'south-central'
  | 'mountain-west'
  | 'southwest'
  | 'pacific-northwest'
  | 'california'
  | 'alaska'
  | 'hawaii';

export const NATIVE_REGIONS: readonly NativeRegionCode[] = [
  'northeast',
  'mid-atlantic',
  'southeast',
  'florida',
  'midwest',
  'great-plains',
  'south-central',
  'mountain-west',
  'southwest',
  'pacific-northwest',
  'california',
  'alaska',
  'hawaii',
];

const REGION_LABELS: Record<NativeRegionCode, string> = {
  northeast: 'Northeast',
  'mid-atlantic': 'Mid-Atlantic',
  southeast: 'Southeast',
  florida: 'Florida',
  midwest: 'Midwest',
  'great-plains': 'Great Plains',
  'south-central': 'South Central',
  'mountain-west': 'Mountain West',
  southwest: 'Southwest',
  'pacific-northwest': 'Pacific Northwest',
  california: 'California',
  alaska: 'Alaska',
  hawaii: 'Hawaii',
};

export function nativeRegionLabel(code: NativeRegionCode): string {
  return REGION_LABELS[code];
}

export function isNativeRegionCode(value: unknown): value is NativeRegionCode {
  return typeof value === 'string' && (NATIVE_REGIONS as readonly string[]).includes(value);
}

/** A named ecological region, carried whole so a finer classification can replace the code later. */
export interface EcoregionRef {
  name: string;
  /** EPA Level III code, where the source actually supplies one. Never invented to fill the field. */
  code?: string;
}

/* ---------------------------------- Frost ------------------------------------ */

/**
 * The growing season, as the two dates that bound it.
 *
 * Frost dates rather than a season *length*, because the two ends move independently and a plant
 * cares which one moved: a late spring delays leaf-out and bloom, a late autumn extends fruit ripening
 * and pushes leaf drop back. Collapsing them to "180 days" throws away exactly the information the
 * phenology model runs on.
 */
export interface FrostWindow {
  /** Day of year of the average last spring frost. */
  lastSpringFrostDay: number;
  /** Day of year of the average first autumn frost. */
  firstAutumnFrostDay: number;
}

/**
 * Frost-free days between the two dates.
 *
 * A window covering the whole calendar reports 365 rather than 364. The difference is one day and
 * the distinction is not: `estimateFrostWindow` represents a frost-free climate as day 1 to day 365,
 * and a season length that came back one short of the year would put "frost-free" and "very nearly
 * frost-free" on the same footing, which is the one place the figure is asked a yes/no question.
 */
export function growingSeasonDays(frost: FrostWindow): number {
  if (frost.lastSpringFrostDay <= 1 && frost.firstAutumnFrostDay >= DAYS_IN_YEAR) return DAYS_IN_YEAR;
  const span = frost.firstAutumnFrostDay - frost.lastSpringFrostDay;
  return Math.max(0, Math.min(DAYS_IN_YEAR, Math.round(span)));
}

/**
 * Frost dates estimated from a hardiness zone.
 *
 * Zone describes the *depth* of winter and this asks about its *length*, which are different
 * questions with a strong but imperfect correlation — a maritime zone 8 and a continental zone 8 have
 * the same cold snap and very different springs. It is used only where nothing better is available,
 * and a profile carrying real normals simply overwrites it.
 *
 * The anchors below are averages across the continental US at whole zones; half-zones interpolate.
 * Beyond zone 10 the season is frost-free and reported as the full year.
 */
export function estimateFrostWindow(zone: HardinessZone): FrostWindow {
  const anchors: readonly { zone: number; last: number; first: number }[] = [
    { zone: 2, last: 152, first: 249 },
    { zone: 3, last: 141, first: 259 },
    { zone: 4, last: 132, first: 268 },
    { zone: 5, last: 122, first: 279 },
    { zone: 6, last: 110, first: 292 },
    { zone: 7, last: 96, first: 305 },
    { zone: 8, last: 80, first: 318 },
    { zone: 9, last: 55, first: 335 },
    { zone: 10, last: 15, first: 355 },
  ];
  const value = zoneValue(zone);
  const first = anchors[0] as { zone: number; last: number; first: number };
  const last = anchors[anchors.length - 1] as { zone: number; last: number; first: number };
  if (value <= first.zone) return { lastSpringFrostDay: first.last, firstAutumnFrostDay: first.first };
  if (value >= last.zone) return { lastSpringFrostDay: 1, firstAutumnFrostDay: DAYS_IN_YEAR };

  for (let i = 0; i < anchors.length - 1; i += 1) {
    const lower = anchors[i] as { zone: number; last: number; first: number };
    const upper = anchors[i + 1] as { zone: number; last: number; first: number };
    if (value < lower.zone || value > upper.zone) continue;
    const t = (value - lower.zone) / (upper.zone - lower.zone);
    return {
      lastSpringFrostDay: Math.round(lower.last + (upper.last - lower.last) * t),
      firstAutumnFrostDay: Math.round(lower.first + (upper.first - lower.first) * t),
    };
  }
  return { lastSpringFrostDay: 110, firstAutumnFrostDay: 292 };
}

/* -------------------------------- Site conditions ------------------------------ */

/**
 * What the designer has done to the site, as distinct from what the climate does to it.
 *
 * The third tier of the resolution chain: master flora → regional/climate → **site conditions** →
 * plant-instance overrides. These are decisions rather than measurements, so they are stated rather
 * than looked up, and they are all optional — an absent field means "nothing has been decided",
 * which is not the same as a default having been chosen.
 */
export interface SiteConditions {
  /** Irrigation lifts the moisture the site can offer, which changes a dry-mismatch warning. */
  irrigated?: boolean;
  /** A walled garden, a south wall, a city centre — worth roughly half a zone each way. */
  shelter?: 'exposed' | 'typical' | 'sheltered';
}

/**
 * An edit to {@link SiteConditions} where an explicit `undefined` *clears* the field.
 *
 * The distinction is the reason this type exists rather than a `Partial`: un-ticking "sheltered" has
 * to erase the decision, not set it to some neutral value, because "nobody has said" is a state the
 * resolver treats differently from "somebody said typical".
 */
export type SiteConditionsPatch = { [K in keyof SiteConditions]?: SiteConditions[K] | undefined };

/**
 * Effective hardiness after site conditions, in zone values.
 *
 * A sheltered courtyard really does overwinter a plant its postcode says it should not, and an
 * exposed hilltop really does kill one it says is safe. Half a zone is the conservative reading of
 * both, and it is applied to the *site* rather than to the plant so one adjustment covers everything
 * planted there.
 */
export function effectiveZoneValue(localization: SiteLocalization): number {
  const base = zoneValue(localization.hardinessZone);
  switch (localization.conditions?.shelter) {
    case 'sheltered':
      return base + 0.5;
    case 'exposed':
      return base - 0.5;
    default:
      return base;
  }
}

/* ------------------------------ The profile itself ----------------------------- */

export interface SiteLocalization {
  schemaVersion: number;
  /** What the user typed. Kept so the UI can show it and a re-resolve can repeat it. */
  postalCode?: string;
  /** ISO 3166-1 alpha-2. Only `US` resolves from a postal code today. */
  countryCode: string;
  latitude: number;
  longitude: number;
  hardinessZone: HardinessZone;
  frost: FrostWindow;
  /** What to call this place in one line — `Central New Jersey`. */
  regionLabel: string;
  /** The bucket nativity is judged against. */
  nativeRegion: NativeRegionCode;
  ecoregion?: EcoregionRef;
  /**
   * AHS heat zone (1–12): average annual days above 30 °C. Absent where unknown, and the heat
   * warnings simply do not fire rather than being guessed at from the cold zone.
   */
  heatZone?: number;
  elevationMeters?: number;
  conditions?: SiteConditions;
  source: LocalizationSource;
  /** ISO 8601. A profile is a reading of a data set at a moment, and says so. */
  resolvedAt: string;

  /* ----------------------------- Not yet supplied ----------------------------- */
  // Every field below is an extension point rather than an omission: the resolver already asks the
  // profile for its parameters, so adding one of these means teaching one model to read it — not
  // reshaping flora records, project files or the UI.

  /** Accumulated Growing Degree Days by the average last frost, base 10 °C. */
  growingDegreeDays?: number;
  /** Winter chill accumulation in hours below 7 °C — what fruiting shrubs actually count. */
  winterChillHours?: number;
  /** Mean annual precipitation, mm. */
  annualPrecipitationMm?: number;
  /** Days per year the profile expects the ground to be frozen. */
  hardFrostDays?: number;
}

/** Growing-season length for a profile, in frost-free days. */
export function seasonLength(localization: SiteLocalization): number {
  return growingSeasonDays(localization.frost);
}

/**
 * The reference climate every canonical flora record is authored against.
 *
 * The existing catalog's phenology days — leaf-out 100, autumn colour 280, leaf drop 315 — were
 * written for a temperate northeastern garden, and that is zone 6b whether or not anyone said so.
 * Naming it here turns those numbers from unlabelled constants into a measurement with a place
 * attached, which is the whole reason the resolver can shift them somewhere else.
 *
 * A record may state its own `phenology.referenceZone`; this is what is assumed when it does not.
 */
export const REFERENCE_ZONE: HardinessZone = { number: 6, band: 'b' };

/** Frost window the baseline catalog days describe. */
export function referenceFrostWindow(): FrostWindow {
  return estimateFrostWindow(REFERENCE_ZONE);
}

/**
 * A profile for a site nobody has localized.
 *
 * Deliberately identical to the reference climate, and marked `assumed`. That is what makes
 * localization additive: an unlocalized site resolves every plant against the very climate its
 * catalog days were written for, so nothing shifts, no warning fires, and the app behaves exactly as
 * it did before the feature existed. See `FloraResolver`.
 */
export function defaultLocalization(): SiteLocalization {
  return {
    schemaVersion: LOCALIZATION_SCHEMA_VERSION,
    countryCode: 'US',
    latitude: 40.2,
    longitude: -74.7,
    hardinessZone: { ...REFERENCE_ZONE },
    frost: referenceFrostWindow(),
    regionLabel: 'Unlocalized site',
    nativeRegion: 'mid-atlantic',
    source: {
      id: 'default',
      label: 'Not localized',
      confidence: 'assumed',
    },
    resolvedAt: new Date(0).toISOString(),
  };
}

/** Whether a profile came from somewhere, as opposed to being the standing default. */
export function isLocalized(localization: SiteLocalization | null | undefined): boolean {
  return localization !== null && localization !== undefined && localization.source.id !== 'default';
}

/**
 * A short, stable key for everything about a profile the resolver's output depends on.
 *
 * This is the cache key half of `floraId + profile + dataVersion`. It deliberately excludes the
 * postal code, the label and the timestamp: two ZIPs that land on the same zone, the same frost dates
 * and the same region resolve every plant identically, and giving them separate cache entries would
 * double the work to store the same answers twice.
 */
export function localizationKey(localization: SiteLocalization): string {
  return [
    localization.countryCode,
    zoneLabel(localization.hardinessZone),
    localization.frost.lastSpringFrostDay,
    localization.frost.firstAutumnFrostDay,
    localization.nativeRegion,
    localization.heatZone ?? '-',
    localization.conditions?.shelter ?? '-',
    localization.conditions?.irrigated === true ? 'irr' : '-',
  ].join('|');
}

/* -------------------------------- Description --------------------------------- */

/** `12 May` — the form the localization summary and the ecology calendar both read in. */
export function formatDay(dayOfYear: number): string {
  const { month, day } = dateFromDayOfYear(dayOfYear);
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${day} ${months[month - 1] ?? ''}`.trim();
}

/** One line for the growing season: `Mid-April to late October · 195 frost-free days`. */
export function describeGrowingSeason(localization: SiteLocalization): string {
  const days = seasonLength(localization);
  if (days >= DAYS_IN_YEAR) return 'Frost-free year round';
  return (
    `${formatDay(localization.frost.lastSpringFrostDay)} to ` +
    `${formatDay(localization.frost.firstAutumnFrostDay)} · ${days} frost-free days`
  );
}

/** One line for the winter: `Zone 6b · winters down to about −18 °C`. */
export function describeWinter(localization: SiteLocalization): string {
  const zone = localization.hardinessZone;
  return `Zone ${zoneLabel(zone)} · winters down to about ${zoneMinimumTempC(zone)} °C`;
}

/* ------------------------------ Serialization -------------------------------- */

/** Deep copy to a JSON-safe object, stamped with the current schema version. */
export function serializeLocalization(localization: SiteLocalization): SiteLocalization {
  return {
    ...localization,
    schemaVersion: LOCALIZATION_SCHEMA_VERSION,
    hardinessZone: { ...localization.hardinessZone },
    frost: { ...localization.frost },
    source: { ...localization.source },
    ...(localization.ecoregion ? { ecoregion: { ...localization.ecoregion } } : {}),
    ...(localization.conditions ? { conditions: { ...localization.conditions } } : {}),
  };
}

/**
 * Rebuild a profile from arbitrary parsed JSON.
 *
 * Defensive rather than strict, and never throwing: a profile is a *convenience*, and a site whose
 * localization failed to parse is still a complete site. An unreadable field falls back to the
 * default, which is the unlocalized reading — the one state that is always safe.
 */
export function deserializeLocalization(raw: unknown): SiteLocalization | undefined {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined;
  const data = raw as Record<string, unknown>;
  const base = defaultLocalization();

  const zoneRaw = data.hardinessZone;
  let zone = base.hardinessZone;
  if (typeof zoneRaw === 'string') {
    zone = parseZone(zoneRaw) ?? zone;
  } else if (typeof zoneRaw === 'object' && zoneRaw !== null) {
    const record = zoneRaw as Record<string, unknown>;
    const number = Math.trunc(Number(record.number));
    if (Number.isFinite(number) && number >= 1 && number <= 13) {
      zone = { number, band: record.band === 'b' ? 'b' : 'a' };
    }
  }

  const frostRaw = data.frost as Record<string, unknown> | undefined;
  const estimated = estimateFrostWindow(zone);
  const frost: FrostWindow = {
    lastSpringFrostDay: clampDay(Number(frostRaw?.lastSpringFrostDay), estimated.lastSpringFrostDay),
    firstAutumnFrostDay: clampDay(Number(frostRaw?.firstAutumnFrostDay), estimated.firstAutumnFrostDay),
  };

  const sourceRaw = data.source as Record<string, unknown> | undefined;
  const confidence = sourceRaw?.confidence;
  const source: LocalizationSource = {
    id: text(sourceRaw?.id) ?? 'imported',
    label: text(sourceRaw?.label) ?? 'Imported profile',
    confidence:
      confidence === 'measured' || confidence === 'estimated' || confidence === 'assumed'
        ? confidence
        : 'estimated',
  };
  const attribution = text(sourceRaw?.attribution);
  if (attribution) source.attribution = attribution;

  const localization: SiteLocalization = {
    schemaVersion: LOCALIZATION_SCHEMA_VERSION,
    countryCode: text(data.countryCode) ?? base.countryCode,
    latitude: clampNumber(Number(data.latitude), -90, 90, base.latitude),
    longitude: clampNumber(Number(data.longitude), -180, 180, base.longitude),
    hardinessZone: zone,
    frost,
    regionLabel: text(data.regionLabel) ?? base.regionLabel,
    nativeRegion: isNativeRegionCode(data.nativeRegion) ? data.nativeRegion : base.nativeRegion,
    source,
    resolvedAt: text(data.resolvedAt) ?? new Date().toISOString(),
  };

  const postalCode = text(data.postalCode);
  if (postalCode) localization.postalCode = postalCode;

  const ecoregion = data.ecoregion as Record<string, unknown> | undefined;
  const ecoName = text(ecoregion?.name);
  if (ecoName) {
    const code = text(ecoregion?.code);
    localization.ecoregion = { name: ecoName, ...(code ? { code } : {}) };
  }

  const heatZone = Number(data.heatZone);
  if (Number.isFinite(heatZone) && heatZone >= 1 && heatZone <= 12) {
    localization.heatZone = Math.round(heatZone);
  }
  const elevation = Number(data.elevationMeters);
  if (Number.isFinite(elevation)) localization.elevationMeters = elevation;

  const conditions = data.conditions as Record<string, unknown> | undefined;
  if (conditions) {
    const resolved: SiteConditions = {};
    if (conditions.irrigated === true) resolved.irrigated = true;
    const shelter = conditions.shelter;
    if (shelter === 'exposed' || shelter === 'sheltered' || shelter === 'typical') {
      resolved.shelter = shelter;
    }
    if (Object.keys(resolved).length > 0) localization.conditions = resolved;
  }

  // Future parameters are read back when present so a file written by a build that has them is not
  // stripped by one that does not.
  for (const key of ['growingDegreeDays', 'winterChillHours', 'annualPrecipitationMm', 'hardFrostDays'] as const) {
    const value = Number(data[key]);
    if (Number.isFinite(value) && value >= 0) localization[key] = value;
  }

  return localization;
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function clampDay(value: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(DAYS_IN_YEAR, Math.max(1, Math.round(value)));
}

function clampNumber(value: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}
